import React, { useState, useEffect } from 'react';
import { Result, Layout, Typography, Space } from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import { useGlobals } from '@shared/contexts/GlobalsContext';
import { location } from '../../../env/browser';
import createQueryConfig from '../../../queries';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { t } from '../../../utils/i18n';
import type { SubscriptionResponse } from '@shore/types';
import type {
  CheckoutUrlPayload,
  CheckoutUrlResponse,
  SubscriptionMfeProps,
} from '../types';

const iframeStyle = {
  flex: 1,
  height: '100%',
  border: 'unset',
};

const CHECKOUT_SUCCESS_REDIRECT_URL = `${location.origin}/success-checkout`;

function SubscriptionUnavailable() {
  return (
    <Layout>
      <Layout.Content>
        <Result
          status="warning"
          title={
            <Space direction="vertical">
              <Typography.Title level={4}>
                {t('subscription-checkout.title')}
              </Typography.Title>
              <Typography.Text>
                {t('subscription-checkout.description')}
              </Typography.Text>
            </Space>
          }
        />
      </Layout.Content>
    </Layout>
  );
}

function Checkout({ merchantId, locale, token }: SubscriptionMfeProps) {
  const [isLoading, toggleLoading] = useState(true);
  const [checkoutUrlResponse, setCheckoutUrlResponse] =
    useState<CheckoutUrlResponse | null>(null);
  const { apiClient } = useGlobals();

  function createCheckoutSession() {
    return apiClient.subscriptionV3.createCheckoutSession(
      {
        merchantId,
        locale,
        productFamily: 'shore_bm',
        redirectUrl: CHECKOUT_SUCCESS_REDIRECT_URL,
        metadata: {
          app: 'calendar-web',
          channel: 'self-service',
        },
      },
      token
    );
  }

  function handleCheckoutUrlResponse(response: CheckoutUrlResponse) {
    setCheckoutUrlResponse(response);
    toggleLoading(false);
  }

  const { mutate: createCheckoutLink } = useMutation({
    mutationFn: createCheckoutSession,
    retry: 3,
    retryDelay: 3000,
    onSuccess: (res) => {
      res
        .json()
        .then(({ checkout_url: checkoutUrl }: CheckoutUrlPayload) => {
          handleCheckoutUrlResponse([false, checkoutUrl]);
        })
        .catch(() => {
          handleCheckoutUrlResponse([true, null]);
        });
    },
    onError: (error) => {
      console.error('Failed to create checkout session: ', error);

      handleCheckoutUrlResponse([true, null]);
    },
  });

  useEffect(() => {
    createCheckoutLink();
  }, [createCheckoutLink]);

  if (isLoading) {
    return null;
  }

  const [error, checkoutUrl] = checkoutUrlResponse ?? [false, null];

  if (error) {
    return <p>Failed to load subscription checkout</p>;
  }

  return (
    <iframe
      title="Shore's subscription checkout"
      style={iframeStyle}
      src={checkoutUrl ?? undefined}
    />
  );
}

export function SubscriptionCheckout() {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { data: token, status: tokenQueryStatus } = useQuery(
    queryConfig.authToken()
  );
  const { status: subscriptionQueryStatus, data: subscription } =
    useQuery<SubscriptionResponse>(queryConfig.subscription(merchantSettings));

  const isLoading =
    isLoadingMerchantSettings ||
    [subscriptionQueryStatus, tokenQueryStatus].includes('pending');

  if (isLoading) {
    return null;
  }

  if (tokenQueryStatus === 'error') {
    return <p>Session expired</p>;
  }

  if (subscriptionQueryStatus === 'error') {
    return (
      <p>Failed to get subscription information. Please try again later.</p>
    );
  }
  const isSubscriptionActivationAllowed =
    subscription?.activation_allowed ?? false;
  const isSubscriptionInactive = ['cancelled', 'paused'].includes(
    subscription?.status ?? ''
  );

  if (!isSubscriptionActivationAllowed) {
    if (isSubscriptionInactive) {
      return <SubscriptionUnavailable />;
    }

    return <Navigate to="/" />;
  }

  const { merchantId, locale } = merchantSettings;

  if (!merchantId) {
    return null;
  }

  const subscriptionPartner = subscription?.customer?.partner;

  const isHeroldPartner = subscriptionPartner?.toLowerCase() === 'herold';

  if (isHeroldPartner) {
    if (subscription?.status === 'active') {
      return <Navigate to="/" />;
    }

    if (['in_trial', 'cancelled'].includes(subscription?.status ?? '')) {
      return <SubscriptionUnavailable />;
    }
  }

  if (subscription?.status === 'active') {
    return <Navigate to="/settings/subscription" />;
  }

  return <Checkout merchantId={merchantId} locale={locale} token={token!} />;
}
